import { useState, useMemo, useEffect } from 'react';
import type { Farmer, RiskProfile } from '../../../../types';
import { refreshFarmer, getFarmerKyc, getKycLabel, getRiskProfileByFarmer, getDebtsByFarmer, getFinancesByFarmer, updateRiskProfile, addRiskProfile, getFarmerRiskProfile, computeCompositeScore, getFarmerRiskSignals } from '../../../../store';
import { listEkycHistoryByFarmer, type BackendEkycRecord } from '../../../../api/ekyc';
import { useFileUrls } from '../../../../hooks/useFileUrls';
import { StatusBadge } from '../../../../components/base/StatusBadge';
import { showToast } from '../../../../components/base/Toast';
import { formatDate, formatDateTime, formatVND } from '../../../../utils/format';
import { useStore } from '../../../../hooks/useStore';
import { useAuth } from '../../../../hooks/useAuth';
import EkycFlow from '../../../../components/feature/EkycFlow';
import { DEBT_STATUS, getCreditScoreColor, getCreditScoreBg, getCreditScoreBorder } from '../../../../utils/status';
import { getScoreBand, getScoreBarPercent, getScoreBarColor, SCORE_BANDS } from '../../../../utils/creditScore';

interface DetailEkycRiskTabProps {
  farmer: Farmer;
}

// Matches agrida-admin's RiskTab.tsx FACTOR_LABELS exactly — same 5C engine,
// same wording, so a factor score means the same thing in both portals.
const FIVE_C_LABELS = {
  capacity: 'C1 · Năng lực trả nợ',
  character: 'C2 · Uy tín/lịch sử',
  capital: 'C3 · Vốn tự có',
  collateral: 'C4 · Tài sản đảm bảo',
  conditions: 'C5 · Điều kiện canh tác',
} as const;

const RISK_TIER_CLASS: Record<string, string> = {
  A: 'bg-agrida-50 text-agrida-700 border-agrida-200',
  B: 'bg-teal-50 text-teal-700 border-teal-200',
  C: 'bg-amber-50 text-amber-700 border-amber-200',
  D: 'bg-red-50 text-red-700 border-red-200',
};

// Vietnamese-readable names for the raw sourceEventType strings the risk
// engine records — this is the literal, concrete proof that "upload tài
// liệu"/"cập nhật eKYC" actually feed the 5C score, not just a claim.
const SIGNAL_EVENT_LABELS: Record<string, string> = {
  'ekyc.verified': 'Xác thực eKYC',
  'farmer.attachment_added': 'Tải lên tài liệu hồ sơ',
  'harvest.confirmed': 'Xác nhận thu hoạch',
  'debt.settled': 'Tất toán khoản vay',
  'collection.settled': 'Thu nợ thành công',
  'risk.cic_checked': 'Kiểm tra CIC',
  'loan.carryover_flagged': 'Gia hạn/trễ hạn khoản vay',
  'contract.signed': 'Ký hợp đồng',
  'field.verification_refused': 'Từ chối xác minh thửa đất',
  'loan.application_submitted': 'Nộp đơn vay',
  'transaction.settled': 'Giao dịch hoàn tất',
  'field.geometry.verified': 'Xác minh ranh giới thửa đất',
  'loan.collateral_added': 'Bổ sung tài sản đảm bảo',
  'farm_log.created': 'Ghi nhật ký canh tác',
  'market.price_updated': 'Cập nhật giá thị trường',
};

type FactorKey = keyof typeof FIVE_C_LABELS;

const FACTOR_TAG_TO_KEY: Record<string, FactorKey> = {
  C1_CAPACITY: 'capacity',
  C2_CHARACTER: 'character',
  C3_CAPITAL: 'capital',
  C4_COLLATERAL: 'collateral',
  C5_CONDITIONS: 'conditions',
};

// Mirrors the backend's seeded SignalMappingRule set exactly
// (backend/src/seed/seed.ts, seedSignalMappingRules) — the real, complete list
// of what can move each factor, not a guess. An event type absent from a
// farmer's history shows as "chưa có" rather than being left unexplained.
const FACTOR_CHECKLIST: Record<FactorKey, string[]> = {
  capacity: ['debt.settled', 'collection.settled', 'harvest.confirmed'],
  character: ['ekyc.verified', 'farmer.attachment_added', 'risk.cic_checked', 'loan.carryover_flagged', 'contract.signed', 'field.verification_refused'],
  capital: ['loan.application_submitted', 'transaction.settled'],
  collateral: ['field.geometry.verified', 'loan.collateral_added'],
  conditions: ['farm_log.created', 'market.price_updated'],
};

export default function DetailEkycRiskTab({ farmer }: DetailEkycRiskTabProps) {
  useStore();
  const kyc = getFarmerKyc(farmer);
  const existing = getRiskProfileByFarmer(farmer.id);
  const kycLabel = getKycLabel(kyc.kycStatus);
  const { user } = useAuth();
  const [ekycOnBehalfOpen, setEkycOnBehalfOpen] = useState(false);
  // HTX may run eKYC for its own member farmer — typically a profile it created
  // before the farmer has an account. It is not a gate for anything; a
  // verified identity only adds to the farmer's credit score (and lets a TPBank
  // sub-account be opened for disbursement).
  const canEkycOnBehalf = user?.role === 'Hợp tác xã' && farmer.membershipStatus === 'Đã xác nhận';
  const [editing, setEditing] = useState(false);
  const [expandedFactor, setExpandedFactor] = useState<FactorKey | null>(null);
  const [mucDo, setMucDo] = useState<RiskProfile['mucDo']>(existing?.mucDo || 'Tốt');
  const [moTa, setMoTa] = useState(existing?.moTa || '');

  const isVerified = kyc.kycStatus === 'ekyc_verified';
  const needsMore = kyc.kycStatus === 'ekyc_need_more';
  const isFailed = kyc.kycStatus === 'ekyc_failed';
  const isPending = kyc.kycStatus === 'ekyc_pending';

  const debts = getDebtsByFarmer(farmer.id);
  const finances = getFinancesByFarmer(farmer.id);

  // Real 5C risk engine (backend risk module) — replaces the disconnected
  // ad-hoc 5-factor score this tab used to compute purely from local mock
  // data. `undefined` = still loading; `null` = farmer has no RiskProfile
  // yet (nothing scored it — no qualifying signal has fired for them).
  const riskProfile = getFarmerRiskProfile(farmer.id);
  const compositeScore = riskProfile ? computeCompositeScore(riskProfile.factors) : undefined;
  const displayTier = riskProfile?.assignedTier ?? riskProfile?.suggestedTier;
  const riskSignals = getFarmerRiskSignals(farmer.id);

  // Credit score band
  const scoreBand = getScoreBand(kyc.creditScore);
  const scoreBarPercent = getScoreBarPercent(kyc.creditScore);
  const scoreBarColor = getScoreBarColor(kyc.creditScore);

  const handleSaveRisk = () => {
    const now = new Date().toISOString();
    if (existing) updateRiskProfile(existing.id, { mucDo, moTa, ngayCapNhat: now });
    else addRiskProfile({ farmerId: farmer.id, mucDo, moTa, ngayCapNhat: now });
    setEditing(false);
    showToast('Đã cập nhật risk profile', 'success');
  };

  const scoreColor = getCreditScoreColor(kyc.creditScore);
  const scoreBg = getCreditScoreBg(kyc.creditScore);

  const hasOcr = !!(kyc.ocrName || kyc.ocrDob || kyc.ocrGender || kyc.ocrAddress);
  const selfieLabel = kyc.selfieResult || 'Chưa có';

  // The farmer's most recent eKYC submission, any decision — used only to
  // surface the actual uploaded photos below; the status text above already
  // comes from getFarmerKyc. listPendingEkycByHtx can't be reused here since
  // it drops a record the moment it's decided (VERIFIED/FAILED).
  const [latestEkyc, setLatestEkyc] = useState<BackendEkycRecord | null | undefined>(undefined);
  useEffect(() => {
    let cancelled = false;
    setLatestEkyc(undefined);
    listEkycHistoryByFarmer(farmer.id)
      .then((records) => { if (!cancelled) setLatestEkyc(records[0] ?? null); })
      .catch(() => { if (!cancelled) setLatestEkyc(null); });
    return () => { cancelled = true; };
  }, [farmer.id]);
  const documentUrls = useFileUrls([latestEkyc?.idFrontFileId, latestEkyc?.idBackFileId, latestEkyc?.selfieFileId]);

  const paymentStats = useMemo(() => {
    const completed = finances.filter((f) => f.loai === 'Chi' && f.trangThai === 'Hoàn thành');
    const settled = debts.filter((d) => d.status === DEBT_STATUS.SETTLED).length;
    const totalPaid = completed.reduce((s, f) => s + f.soTien, 0);
    return { onTime: completed.length, settled, totalPaid };
  }, [finances, debts]);

  return (
    <div className="space-y-4">
      {/* ── eKYC Section ── */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 md:p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-slate-900">Trạng thái eKYC</h3>
          <StatusBadge status={kycLabel} />
        </div>

        {isVerified && <div className="p-3 rounded-xl bg-agrida-50 border border-agrida-100 mb-4 text-sm text-agrida-800 font-medium">Nông hộ đã hoàn tất xác thực eKYC</div>}
        {isPending && <div className="p-3 rounded-xl bg-sky-50 border border-sky-100 mb-4 text-sm text-sky-800 font-medium">Hồ sơ eKYC đang được hệ thống xử lý.</div>}
        {needsMore && <div className="p-3 rounded-xl bg-amber-50 border border-amber-100 mb-4"><p className="text-sm text-amber-800 font-medium">Cần bổ sung thông tin eKYC</p>{kyc.kycReason && <p className="text-xs text-amber-700 mt-1"><span className="font-medium">Lý do:</span> {kyc.kycReason}</p>}</div>}
        {isFailed && <div className="p-3 rounded-xl bg-red-50 border border-red-100 mb-4"><p className="text-sm text-red-800 font-medium">eKYC thất bại</p>{kyc.kycReason && <p className="text-xs text-red-700 mt-1"><span className="font-medium">Lý do:</span> {kyc.kycReason}</p>}</div>}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div><p className="text-xs text-slate-500">Ngày xác thực</p><p className="text-sm font-medium text-slate-800 mt-0.5">{kyc.verifiedAt ? formatDate(kyc.verifiedAt) : '—'}</p></div>
          <div><p className="text-xs text-slate-500">Số CCCD</p><p className="text-sm font-medium text-slate-800 mt-0.5">{kyc.cccdMask || '—'}</p></div>
          <div><p className="text-xs text-slate-500">Credit score</p><p className={`text-lg font-bold ${scoreColor}`}>{kyc.creditScore ?? '—'}</p></div>
          <div><p className="text-xs text-slate-500">Kết quả selfie</p><span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mt-0.5 ${selfieLabel === 'Khớp' ? 'bg-agrida-100 text-agrida-700' : selfieLabel === 'Không khớp' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-500'}`}>{selfieLabel}</span></div>
        </div>

        {/* OCR details */}
        {hasOcr && (
          <div className="mt-3 pt-3 border-t border-slate-100">
            <p className="text-xs text-slate-500 mb-2">Kết quả OCR</p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {kyc.ocrName && <div><span className="text-slate-400">Tên CCCD:</span> <span className="font-medium text-slate-700 ml-1">{kyc.ocrName}</span></div>}
              {kyc.ocrDob && <div><span className="text-slate-400">Ngày sinh:</span> <span className="font-medium text-slate-700 ml-1">{kyc.ocrDob}</span></div>}
              {kyc.ocrGender && <div><span className="text-slate-400">Giới tính:</span> <span className="font-medium text-slate-700 ml-1">{kyc.ocrGender}</span></div>}
              {kyc.ocrIssueDate && <div><span className="text-slate-400">Ngày cấp:</span> <span className="font-medium text-slate-700 ml-1">{kyc.ocrIssueDate}</span></div>}
              {kyc.ocrAddress && <div className="col-span-2"><span className="text-slate-400">Địa chỉ:</span> <span className="font-medium text-slate-700 ml-1">{kyc.ocrAddress}</span></div>}
            </div>
          </div>
        )}

        {/* What the server-side reading found when the CCCD was submitted:
            MRZ check digits, number vs birth year, front vs back, and any
            field the submitter changed from what AI read. */}
        {latestEkyc?.ocrResult && (() => {
          const ocr = latestEkyc.ocrResult as Record<string, unknown>;
          const checks = Array.isArray(ocr.aiChecks) ? (ocr.aiChecks as Array<{ code: string; ok: boolean; label: string }>) : [];
          const edited = Array.isArray(ocr.editedAfterAi) ? (ocr.editedAfterAi as string[]) : [];
          const source = typeof ocr.aiSource === 'string' ? ocr.aiSource : undefined;
          if (!source && checks.length === 0) return null;
          const EDITED_LABEL: Record<string, string> = { hoTen: 'Họ tên', soCccd: 'Số CCCD', ngaySinh: 'Ngày sinh', gioiTinh: 'Giới tính', diaChi: 'Địa chỉ', ngayCap: 'Ngày cấp' };
          return (
            <div className="mt-3 pt-3 border-t border-slate-100">
              <p className="text-xs text-slate-500 mb-2">
                Kiểm tra tự động khi nộp
                <span className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${source === 'AI' ? 'bg-sky-100 text-sky-700' : 'bg-amber-100 text-amber-700'}`}>
                  {source === 'AI' ? 'AI đọc ảnh' : source === 'MOCK' ? 'Dữ liệu mẫu (chưa có AI)' : 'Nhập tay'}
                </span>
              </p>
              <ul className="space-y-1">
                {checks.map((c) => (
                  <li key={c.code + c.label} className={`flex items-start gap-1.5 text-xs ${c.ok ? 'text-agrida-700' : 'text-amber-700'}`}>
                    <i className={`mt-px ${c.ok ? 'ri-checkbox-circle-fill' : 'ri-error-warning-fill'}`} />
                    <span>{c.label}</span>
                  </li>
                ))}
                {edited.length > 0 && (
                  <li className="flex items-start gap-1.5 text-xs text-amber-700">
                    <i className="mt-px ri-edit-2-fill" />
                    <span>Người nộp đã sửa so với AI đọc: {edited.map((k) => EDITED_LABEL[k] ?? k).join(', ')}</span>
                  </li>
                )}
              </ul>
            </div>
          );
        })()}

        {/* Document photos — the actual uploaded CCCD/selfie images, not just
            their masked/OCR text above, so a reviewer can look them up without
            leaving this tab. */}
        <div className="mt-3 pt-3 border-t border-slate-100">
          <p className="text-xs text-slate-500 mb-2">Tài liệu đã tải lên</p>
          {latestEkyc === undefined ? (
            <p className="text-xs text-slate-400">Đang tải...</p>
          ) : latestEkyc === null ? (
            <p className="text-xs text-slate-400">Chưa có hồ sơ eKYC nào được nộp.</p>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {([[latestEkyc.idFrontFileId, 'Mặt trước'], [latestEkyc.idBackFileId, 'Mặt sau'], [latestEkyc.selfieFileId, 'Chân dung']] as const).map(([fileId, label]) => (
                <div key={label} className="text-center">
                  {fileId && documentUrls[fileId] ? (
                    <a href={documentUrls[fileId]} target="_blank" rel="noopener noreferrer" title={`Xem ${label} kích thước đầy đủ`}>
                      <img src={documentUrls[fileId]} alt={label} className="w-full aspect-square rounded-xl object-cover border border-slate-200" />
                    </a>
                  ) : (
                    <div className="w-full aspect-square rounded-xl border border-dashed border-slate-200 bg-slate-50 flex items-center justify-center text-slate-300">
                      <i className={fileId ? 'ri-loader-4-line animate-spin' : 'ri-image-line text-lg'} />
                    </div>
                  )}
                  <p className="mt-1 text-[10px] text-slate-500">{label}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Action */}
        <div className="mt-4 flex gap-3">
          {canEkycOnBehalf && (
            <button
              type="button"
              onClick={() => setEkycOnBehalfOpen(true)}
              className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors whitespace-nowrap min-h-[44px] ${
                isVerified ? 'bg-slate-100 hover:bg-slate-200 text-slate-700' : 'bg-agrida-700 hover:bg-agrida-800 text-white'
              }`}
            >
              {isVerified ? 'Làm lại eKYC hộ nông hộ' : 'Làm eKYC hộ nông hộ'}
            </button>
          )}
        </div>
        {canEkycOnBehalf && !isVerified && (
          <p className="mt-2 text-xs text-slate-500">
            Không bắt buộc — eKYC chỉ giúp cộng điểm tín nhiệm cho nông hộ. Chụp CCCD hai mặt và ảnh chân dung của nông hộ, hồ sơ được xác thực ngay.
          </p>
        )}
      </div>

      {ekycOnBehalfOpen && (
        <div className="fixed inset-0 z-[97] flex items-end sm:items-center justify-center bg-black/40">
          <div className="flex h-[100dvh] w-full flex-col bg-white sm:h-[90vh] sm:max-w-md sm:rounded-2xl overflow-hidden">
            <div className="flex h-12 shrink-0 items-center justify-between border-b border-slate-100 px-4">
              <p className="text-sm font-semibold text-slate-900">eKYC hộ: {farmer.name}</p>
              <button type="button" onClick={() => setEkycOnBehalfOpen(false)} className="text-slate-400" aria-label="Đóng">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="min-h-0 flex-1">
              <EkycFlow
                onBehalfOf={{
                  farmerId: farmer.id,
                  farmerName: farmer.name,
                  onDone: () => {
                    setEkycOnBehalfOpen(false);
                    refreshFarmer(farmer.id, user?.htxId);
                  },
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Credit Score 5-Band Visualization ── */}
      {kyc.creditScore && (
        <div className="bg-white rounded-xl border border-slate-200 p-4 md:p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-slate-900">Điểm tín dụng — Hệ thống 5 cấp</h3>
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${scoreBg} ${scoreColor} border ${getCreditScoreBorder(kyc.creditScore)}`}>
              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-funds-line text-[10px]" /></span>
              {kyc.creditScore}/900
            </span>
          </div>

          {/* Score bar with 5 bands */}
          <div className="mb-3">
            <div className="h-3 rounded-full overflow-hidden flex">
              <div className="bg-red-400 flex items-center justify-center" style={{ width: '16.67%' }}>
                <span className="text-[8px] text-white font-semibold">Nguy cơ</span>
              </div>
              <div className="bg-orange-400 flex items-center justify-center" style={{ width: '16.67%' }}>
                <span className="text-[8px] text-white font-semibold">Rủi ro</span>
              </div>
              <div className="bg-amber-400 flex items-center justify-center" style={{ width: '16.67%' }}>
                <span className="text-[8px] text-white font-semibold">Chú ý</span>
              </div>
              <div className="bg-teal-400 flex items-center justify-center" style={{ width: '16.67%' }}>
                <span className="text-[8px] text-white font-semibold">Khá</span>
              </div>
              <div className="bg-agrida-400 flex items-center justify-center" style={{ width: '33.33%' }}>
                <span className="text-[8px] text-white font-semibold">Tốt</span>
              </div>
            </div>
            <div className="flex justify-between text-[9px] text-slate-400 mt-1">
              <span>300</span><span>450</span><span>550</span><span>650</span><span>760</span><span>900</span>
            </div>
            {/* Current score marker */}
            <div className="relative mt-1">
              <div className="absolute w-3 h-3 bg-white border-2 border-slate-800 rounded-full transform -translate-x-1/2" style={{ left: `${Math.min(98, scoreBarPercent)}%` }} />
            </div>
          </div>

          {/* Band legend */}
          <div className="grid grid-cols-5 gap-1.5">
            {SCORE_BANDS.map((band) => (
              <div key={band.band} className={`text-center p-1.5 rounded-xl border ${scoreBand.band === band.band ? `${band.bgColor} ${band.borderColor} ring-2 ring-offset-1 ${band.barColor}` : 'border-slate-100 bg-slate-50'}`}>
                <span className="text-[9px] font-semibold">{band.band}</span>
                <p className="text-[8px] text-slate-400">{band.min}–{band.max}</p>
              </div>
            ))}
          </div>

          {/* System recommendation for this score */}
          <div className={`mt-3 p-2.5 rounded-xl border ${scoreBand.bgColor} ${scoreBand.borderColor}`}>
            <p className={`text-xs font-bold ${scoreBand.color}`}>Khuyến nghị: </p>
            <p className="text-[11px] text-slate-600 mt-0.5">{scoreBand.recommendation}</p>
          </div>
        </div>
      )}

      {/* ── Risk Assessment Section — real 5C engine (backend risk module),
          same factors/labels the admin portal's RiskTab already shows;
          replaces the disconnected ad-hoc score this tab used to compute
          locally from diaries/harvests/debts/contracts. ── */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 md:p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Đánh giá rủi ro (5C)</h3>
            {compositeScore !== undefined && (
              <p className="text-xs text-slate-500 mt-0.5">Điểm tổng hợp: {compositeScore.toFixed(1)}/100</p>
            )}
          </div>
          {displayTier && (
            <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${RISK_TIER_CLASS[displayTier]}`}>
              Xếp hạng {displayTier}{riskProfile?.assignedTier ? ' (đã gán)' : ''}
            </span>
          )}
        </div>

        {riskProfile === undefined && <p className="text-sm text-slate-400">Đang tải điểm rủi ro...</p>}

        {riskProfile === null && (
          <div className="p-3 rounded-xl bg-slate-50 border border-slate-100 text-sm text-slate-500">
            Chưa có dữ liệu chấm điểm 5C — hồ sơ sẽ được chấm điểm khi có đủ hoạt động thực tế (nhật ký canh tác, thu hoạch, hợp đồng, thanh toán...).
          </div>
        )}

        {riskProfile && compositeScore !== undefined && (
          <>
            <div className="mb-4">
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all ${compositeScore >= 80 ? 'bg-agrida-500' : compositeScore >= 65 ? 'bg-teal-500' : compositeScore >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${Math.max(4, compositeScore)}%` }} />
              </div>
              <div className="flex justify-between text-xs text-slate-400 mt-1"><span>0 — D</span><span>50 — C</span><span>65 — B</span><span>80 — A</span></div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5">
              {(Object.entries(FIVE_C_LABELS) as Array<[FactorKey, string]>).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setExpandedFactor((cur) => (cur === key ? null : key))}
                  className={`rounded-xl p-2 text-center transition-colors ${expandedFactor === key ? 'bg-agrida-100 ring-2 ring-agrida-400' : 'bg-slate-50 hover:bg-slate-100'}`}
                >
                  <p className="text-[9px] text-slate-500">{label}</p>
                  <p className="mt-1 text-base font-bold text-slate-800">{riskProfile.factors[key].toFixed(0)}</p>
                </button>
              ))}
            </div>
            <p className="mt-1.5 text-[10px] text-slate-400">Bấm vào một nhóm để xem checklist minh chứng và lịch sử cập nhật của nhóm đó.</p>

            {expandedFactor && (() => {
              const checklist = FACTOR_CHECKLIST[expandedFactor];
              const factorSignals = riskSignals.filter((s) => FACTOR_TAG_TO_KEY[s.factorTag] === expandedFactor);
              return (
                <div className="mt-3 rounded-xl border border-agrida-100 bg-agrida-50/40 p-3">
                  <p className="text-xs font-semibold text-slate-800 mb-2">{FIVE_C_LABELS[expandedFactor]} — điểm {riskProfile.factors[expandedFactor].toFixed(0)}/100</p>

                  <p className="text-[10px] font-medium text-slate-500 mb-1">Checklist minh chứng</p>
                  <div className="space-y-1 mb-3">
                    {checklist.map((eventType) => {
                      const matches = factorSignals.filter((s) => s.sourceEventType === eventType);
                      const latest = matches[0];
                      return (
                        <div key={eventType} className="flex items-center justify-between gap-2 text-xs bg-white rounded-lg px-2.5 py-1.5 border border-slate-100">
                          <span className="flex items-center gap-1.5 min-w-0">
                            <i className={latest ? 'ri-checkbox-circle-fill text-agrida-600' : 'ri-checkbox-blank-circle-line text-slate-300'} />
                            <span className={`truncate ${latest ? 'text-slate-700' : 'text-slate-400'}`}>{SIGNAL_EVENT_LABELS[eventType] || eventType}</span>
                          </span>
                          <span className="shrink-0 text-[10px] text-slate-400">{latest ? `${matches.length} lần · ${formatDate(latest.extractedAt)}` : 'chưa có'}</span>
                        </div>
                      );
                    })}
                  </div>

                  <p className="text-[10px] font-medium text-slate-500 mb-1">Lịch sử cập nhật ({factorSignals.length})</p>
                  {factorSignals.length === 0 ? (
                    <p className="text-xs text-slate-400">Chưa có tín hiệu nào cho nhóm này.</p>
                  ) : (
                    <div className="space-y-1">
                      {factorSignals.map((s) => (
                        <div key={s._id} className="flex items-center justify-between gap-2 text-xs bg-white rounded-lg px-2.5 py-1.5 border border-slate-100">
                          <span className="flex items-center gap-1.5 min-w-0 truncate">
                            {s.isRedFlag && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-semibold shrink-0">Cảnh báo</span>}
                            <span className="truncate text-slate-700">{SIGNAL_EVENT_LABELS[s.sourceEventType] || s.sourceEventType}</span>
                          </span>
                          <span className="flex items-center gap-2 shrink-0">
                            <span className="text-slate-400">{formatDateTime(s.extractedAt)}</span>
                            <span className={`font-bold ${s.normalizedScore >= 70 ? 'text-agrida-600' : s.normalizedScore >= 40 ? 'text-amber-600' : 'text-red-600'}`}>{s.normalizedScore.toFixed(0)}</span>
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            {riskProfile.redFlags.length > 0 && (
              <div className="mt-3 p-2.5 rounded-xl bg-red-50 border border-red-100">
                <p className="text-xs font-semibold text-red-700 mb-1">Cảnh báo</p>
                <div className="flex flex-wrap gap-1">
                  {riskProfile.redFlags.map((flag) => (
                    <span key={flag} className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">{flag}</span>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Payment history ── */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 md:p-5">
        <h3 className="text-base font-semibold text-slate-900 mb-3">Lịch sử trả nợ</h3>
        <div className="grid grid-cols-3 gap-3">
          <div className="p-3 rounded-xl bg-agrida-50 text-center"><p className="text-xs text-slate-500">Trả đúng hạn</p><p className="text-lg font-bold text-agrida-700">{paymentStats.onTime}</p></div>
          <div className="p-3 rounded-xl bg-sky-50 text-center"><p className="text-xs text-slate-500">Đã tất toán</p><p className="text-lg font-bold text-sky-700">{paymentStats.settled}</p></div>
          <div className="p-3 rounded-xl bg-amber-50 text-center"><p className="text-xs text-slate-500">Tổng đã trả</p><p className="text-sm font-bold text-amber-700">{formatVND(paymentStats.totalPaid)}</p></div>
        </div>
      </div>

      {/* ── Manual risk editor ── */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 md:p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-slate-900">Đánh giá thủ công</h3>
          {!editing && (
            <button type="button" onClick={() => setEditing(true)} className="px-3 py-2 rounded-md text-sm font-medium bg-agrida-50 text-agrida-700 hover:bg-agrida-100 transition-colors whitespace-nowrap min-h-[44px]">
              <span className="w-4 h-4 inline-flex items-center justify-center mr-1"><i className="ri-edit-line" /></span>Cập nhật
            </button>
          )}
        </div>
        {editing ? (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {(['Tốt', 'Khá', 'Cần chú ý', 'Rủi ro cao', 'Nguy cơ'] as const).map((m) => (
                <button key={m} type="button" onClick={() => setMucDo(m)} className={`px-3 py-1.5 rounded-xl text-sm font-medium border transition-colors ${mucDo === m ? (m === 'Tốt' ? 'bg-agrida-100 border-agrida-300 text-agrida-800' : m === 'Khá' ? 'bg-teal-100 border-teal-300 text-teal-800' : m === 'Cần chú ý' ? 'bg-amber-100 border-amber-300 text-amber-800' : m === 'Rủi ro cao' ? 'bg-orange-100 border-orange-300 text-orange-800' : 'bg-red-100 border-red-300 text-red-800') : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>{m}</button>
              ))}
            </div>
            <textarea value={moTa} onChange={(e) => setMoTa(e.target.value)} rows={3} maxLength={500} className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" placeholder="Nhập nhận xét về rủi ro..." />
            <div className="flex gap-3 justify-end">
              <button type="button" onClick={() => setEditing(false)} className="px-4 py-2 rounded-md text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors whitespace-nowrap min-h-[44px]">Hủy</button>
              <button type="button" onClick={handleSaveRisk} className="px-4 py-2 rounded-md text-sm font-medium bg-agrida-700 hover:bg-agrida-800 text-white transition-colors whitespace-nowrap min-h-[44px]">Lưu</button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center gap-3"><p className="text-sm text-slate-600">Mức risk:</p><StatusBadge status={mucDo} /></div>
            {existing && <p className="text-xs text-slate-400">Cập nhật lần cuối: {formatDate(existing.ngayCapNhat)}</p>}
            {moTa && <div className="p-3 rounded-xl bg-slate-50 border border-slate-100"><p className="text-xs text-slate-500">Nhận xét:</p><p className="text-sm text-slate-700 mt-0.5">{moTa}</p></div>}
          </div>
        )}
      </div>
    </div>
  );
}